import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import {
  type BorderSide,
  type RuntimeProps,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
} from "./shared";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { type WBReveal } from "./reveal";

export function TextSection(props: RuntimeProps) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const buttonAlign =
    (props.buttonAlign as "left" | "center" | "right" | undefined) ?? "center";
  const buttonAlignment =
    (props.buttonAlignment as "flex-row" | "flex-col" | undefined) ??
    "flex-row";
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButtons =
    (props.distTextButtons as string | undefined) ?? "gap-3";
  const distButtons = (props.distButtons as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const isVisibleHeadline =
    (props.isVisibleHeadline as boolean | undefined) ?? true;
  const isVisibleHeader =
    (props.isVisibleHeader as boolean | undefined) ?? true;
  const isVisibleText = (props.isVisibleText as boolean | undefined) ?? true;
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);

  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 flex flex-col w-full",
          alignmentClass,
          isVisibleHeadline || isVisibleHeader || isVisibleText
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex",
            distTextButtons,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse"
                : "flex-col wb-md:flex-row"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={isVisibleHeadline}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={isVisibleHeader}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={isVisibleText}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div
            className={classNames(
              "items-center flex",
              distButtons,
              rightWidth,
              buttonAlignment,
              buttonAlign === "left"
                ? "justify-start"
                : buttonAlign === "center"
                  ? "justify-center"
                  : "justify-end",
            )}
          >
            <Element is={Button} id="buttonOne" />
            <Element is={Button} id="buttonTwo" />
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
